import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { i18n } from 'i18n';
import ToolbarTooltip from './ToolbarTooltip';
import {
  TOOLTIP_DELAY_MS,
  TOOLBAR_MENU_Z_INDEX,
  TOOLBAR_TOOLTIP_Z_INDEX,
  buildTooltipContent
} from './utils';

interface SplitMenuItem {
  key: string;
  label: string;
  description?: string;
  tooltipTitle?: string;
}

interface SplitToolbarButtonProps {
  label: string;
  icon: React.ReactNode;
  onClick: (e?: React.MouseEvent<HTMLElement>) => void;
  onMenuItemClick: (key: string) => void;
  menuItems: SplitMenuItem[];
  disabled?: boolean;
  selectedKey?: string;
  description?: string;
  disabledDescription?: string;
  tooltipTitle?: string;
  selected?: boolean;
}

const SplitToolbarButton = ({
  label,
  icon,
  onClick,
  onMenuItemClick,
  menuItems,
  disabled,
  selectedKey,
  description,
  disabledDescription,
  tooltipTitle,
  selected,
}: SplitToolbarButtonProps) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const effectiveDescription = disabled && disabledDescription ? disabledDescription : description;
  const labelChunk = (
    <Box sx={{ display: 'flex', alignItems: 'center', height: 12 }}>
      <Typography
        variant="caption"
        onClick={disabled ? undefined : onClick}
        sx={{
          fontSize: 10,
          lineHeight: 1.1,
          textAlign: 'center',
          color: disabled ? 'text.disabled' : 'text.primary',
          cursor: disabled ? 'default' : 'pointer',
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>
      <ToolbarTooltip title={i18n._('{tool} Options', { tool: tooltipTitle ?? label })} description={i18n._('Choose a mode from the dropdown.')}>
        <Box
          role="button"
          aria-label={i18n._('{tool} Options', { tool: tooltipTitle ?? label })}
          onClick={disabled ? undefined : openMenu}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: disabled ? 'default' : 'pointer',
            borderRadius: 0.5,
            '&:hover': disabled ? undefined : { bgcolor: 'action.hover' },
          }}
        >
          <ArrowDropDownIcon
            sx={{ fontSize: 16, color: disabled ? 'text.disabled' : 'text.primary' }}
          />
        </Box>
      </ToolbarTooltip>
    </Box>
  );
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 48,
      }}
    >
      <ToolbarTooltip title={tooltipTitle ?? label} description={effectiveDescription}>
        <IconButton
          size="small"
          aria-label={tooltipTitle ?? label}
          onClick={onClick}
          disabled={disabled}
          sx={selected ? {
            bgcolor: 'action.selected',
            color: 'inherit',
            '&:hover': { bgcolor: 'action.selected' },
          } : undefined}
        >
          {icon}
        </IconButton>
      </ToolbarTooltip>
      {labelChunk}
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        sx={{ zIndex: TOOLBAR_MENU_Z_INDEX }}
      >
        {menuItems.map((item) => {
          const menuItem = (
            <MenuItem
              key={item.key}
              selected={selectedKey === item.key}
              onClick={() => {
                setAnchor(null);
                onMenuItemClick(item.key);
              }}
            >
              {item.label}
            </MenuItem>
          );
          if (!item.description && !item.tooltipTitle) return menuItem;
          return (
            <Tooltip
              key={item.key}
              title={buildTooltipContent(item.tooltipTitle ?? item.label, item.description)}
              placement="right"
              arrow
              enterDelay={TOOLTIP_DELAY_MS}
              enterNextDelay={TOOLTIP_DELAY_MS}
              slotProps={{ tooltip: { sx: { zIndex: TOOLBAR_TOOLTIP_Z_INDEX } }, popper: { sx: { zIndex: TOOLBAR_TOOLTIP_Z_INDEX } } }}
            >
              {menuItem}
            </Tooltip>
          );
        })}
      </Menu>
    </Box>
  );
};

export default SplitToolbarButton;
